/**
 * Session slice (P1 auth): /v1/app/me bootstraps the whole shell in one call; login/logout/tenant switch.
 * Nothing but UI conveniences goes to localStorage — the session is the HttpOnly `__Host-rnr_s` cookie.
 */
import { APP, type Http } from './http';
import type { FixtureDb } from './fixtures/db';
import type { Me } from './types';
import { apiMe, notAvailable } from './live/dto';
import { statusOf, stepOfLogin, type SecondStepStatus, type Step } from './secondStep';

/** A new setup: the key (as text and as the link a QR code carries), shown once, and when it lapses. */
export interface Enrolment { secret: string; otpauth_url: string; expires_at: string }

/**
 * The second sign-in step (api/src/modules/auth/mfa-routes.js, under /v1/app/auth/mfa). Present on the server only when
 * the step is switched on there; `me().mfa` says whether it is. `status`, `startEnrol`, `confirmEnrol` and `verify` also
 * work on a session that still owes the step; the rest need one that has passed.
 */
export interface SecondStepApi {
  status(): Promise<SecondStepStatus>;
  /** `password` is needed when the session is more than ten minutes old (403 reauth_required says so). */
  startEnrol(password?: string): Promise<Enrolment>;
  /** → the ten recovery codes, shown once. This session has then passed. */
  confirmEnrol(code: string): Promise<string[]>;
  /** A six-digit code or a recovery code. This session has then passed. */
  verify(code: string): Promise<{ method: string; recovery_codes_left: number }>;
  /** A current six-digit code → a new set of recovery codes; the old set stops working. */
  regenerateRecovery(code: string): Promise<string[]>;
  /** A six-digit code or a recovery code. Refused (409 mfa_required_by_policy) while the store requires the step. */
  disable(code: string): Promise<void>;
  /** The roles this store asks the step of ([] = none). */
  requiredRoles(): Promise<string[]>;
  /** The store's owner only, and only once enrolled (409 mfa_enrol_first). → the roles now required */
  setRequiredRoles(roles: string[]): Promise<string[]>;
}

export interface SessionApi {
  me(): Promise<Me>;
  /** → what the new session still owes before it may open the app (the second sign-in step). */
  login(email: string, password: string): Promise<Step>;
  secondStep: SecondStepApi;
  requestMagicLink(email: string): Promise<void>;
  logout(): Promise<void>;
  resetPassword(email: string): Promise<void>;
  acceptInvite(token: string, password: string, name?: { first_name: string; last_name: string }): Promise<void>;
  switchTenant(tenantId: string): Promise<void>;
}

/** P1 auth (api/src/modules/auth): `/v1/app/me` is `{ok, principal_kind, user, tenant, …}` (auth/me.js), mapped to `Me`. */
export const liveSession = (http: Http): SessionApi => ({
  me: () => http.get<Record<string, any>>(`${APP}/me`).then(apiMe),
  login: (email, password) => http.post<unknown>(`${APP}/auth/login`, { email, password }).then(stepOfLogin),
  secondStep: liveSecondStep(http, `${APP}/auth/mfa`),
  requestMagicLink: (email) => http.post<unknown>(`${APP}/auth/magic_link`, { email }).then(() => undefined),
  logout: () => http.post<unknown>(`${APP}/auth/logout`).then(() => undefined),
  resetPassword: (email) => http.post<unknown>(`${APP}/auth/password/reset`, { email }).then(() => undefined),
  acceptInvite: (token, password, name) => http.post<unknown>(`${APP}/auth/invite/accept`, { token, password, ...name }).then(() => undefined),
  // P1 has no in-session tenant switch; sign in again with `tenant_id` (auth/login accepts it). Rolling out.
  switchTenant: () => notAvailable('tenant_switch'),
});

const codesOf = (r: { recovery_codes?: unknown }) => (Array.isArray(r.recovery_codes) ? r.recovery_codes.map(String) : []);
const rolesOf = (r: { mfa_required_roles?: unknown }) => (Array.isArray(r.mfa_required_roles) ? r.mfa_required_roles.map(String) : []);

/** `prefix` = the routes of one kind of account (store staff here; our own desk has the same set under its own prefix). */
export const liveSecondStep = (http: Http, prefix: string): SecondStepApi => ({
  status: () => http.get<{ mfa?: unknown }>(prefix).then((r) => statusOf(r.mfa) ?? Promise.reject(new Error('second_step_unavailable'))),
  startEnrol: (password) => http.post<Enrolment>(`${prefix}/enrol`, password ? { password } : {}),
  confirmEnrol: (code) => http.post<{ recovery_codes?: unknown }>(`${prefix}/enrol/confirm`, { code }).then(codesOf),
  verify: (code) => http.post<{ method: string; recovery_codes_left: number }>(`${prefix}/verify`, { code }),
  regenerateRecovery: (code) => http.post<{ recovery_codes?: unknown }>(`${prefix}/recovery/regenerate`, { code }).then(codesOf),
  disable: (code) => http.post<unknown>(`${prefix}/disable`, { code }).then(() => undefined),
  requiredRoles: () => http.get<{ mfa_required_roles?: unknown }>(`${APP}/tenant`).then(rolesOf),
  setRequiredRoles: (roles) => http.patch<{ mfa_required_roles?: unknown }>(`${APP}/tenant`, { mfa_required_roles: roles }).then(rolesOf),
});

export const fixtureSession = (db: FixtureDb): SessionApi => {
  const key = 'rnr.fx.signedOut';
  const signedOut = () => { try { return sessionStorage.getItem(key) === '1'; } catch { return false; } };
  const off = async (): Promise<never> => db.fail(404, 'not_found');
  return {
    me: () => (signedOut() ? Promise.reject(db.fail(401, 'unauthorized')) : db.delay(db.me)),
    login: async (email, password) => {
      if (!email || password.length < 4) db.fail(400, 'invalid_credentials');
      try { sessionStorage.removeItem(key); } catch { /* private mode */ }
      await db.delay(null);
      return 'none';
    },
    // The sample tenant runs with the second sign-in step off, as the server answers when it is switched off there.
    secondStep: {
      status: off, startEnrol: off, confirmEnrol: off, verify: off, regenerateRecovery: off, disable: off,
      requiredRoles: () => db.delay([]), setRequiredRoles: off,
    },
    requestMagicLink: () => db.delay(undefined),
    logout: async () => { try { sessionStorage.setItem(key, '1'); } catch { /* ignore */ } await db.delay(null); },
    resetPassword: () => db.delay(undefined),
    acceptInvite: () => db.delay(undefined),
    switchTenant: () => db.delay(undefined),
  };
};
